/**
 * <tm-tabs> — the Details | Nutrition Facts control in
 * sections/tm-product-details.liquid.
 *
 * Markup contract: buttons with role="tab" and aria-controls, panels with
 * role="tabpanel". Both panels are in the HTML from the server; this only
 * decides which one is visible. Without this script the section's <noscript>
 * rule shows both panels stacked, so nothing is ever unreachable.
 *
 * Keyboard: Left/Right/Home/End move between tabs, as the WAI-ARIA tabs
 * pattern expects. A URL hash matching a panel id opens that tab, so
 * "#nutrition-facts" can be linked to.
 */
class TmTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.tabs.length < 2) return;

    this.onClick = this.onClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onClick);
      tab.addEventListener('keydown', this.onKey);
    });

    let initial = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    if (initial < 0) initial = 0;

    const hash = (window.location.hash || '').replace('#', '');
    if (hash) {
      const byHash = this.tabs.findIndex((t) => (t.getAttribute('aria-controls') || '').endsWith(hash));
      if (byHash >= 0) initial = byHash;
    }

    this.select(initial, false);
  }

  disconnectedCallback() {
    (this.tabs || []).forEach((tab) => {
      tab.removeEventListener('click', this.onClick);
      tab.removeEventListener('keydown', this.onKey);
    });
  }

  onClick(event) {
    const index = this.tabs.indexOf(event.currentTarget);
    if (index >= 0) this.select(index, true);
  }

  onKey(event) {
    const current = this.tabs.indexOf(event.currentTarget);
    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    this.select(next, true);
    this.tabs[next].focus();
  }

  select(index, userInitiated) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.classList.toggle('tm-pd__panel--hidden', !active);
    });
    this.dataset.active = String(index);
    if (userInitiated) this.dispatchEvent(new CustomEvent('tm-tabs:change', { detail: { index }, bubbles: true }));
  }
}

if (!customElements.get('tm-tabs')) {
  customElements.define('tm-tabs', TmTabs);
}
